import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download, Mail, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DailyPickupReportCard
 * Lets Property Managers / Admins:
 *  - Pick a date (defaults to yesterday)
 *  - Preview summary
 *  - Download branded PDF
 *  - Email PDF to the property manager
 */
export default function DailyPickupReportCard({ propertyOptions, defaultPropertyId, dataTestId = 'daily-report-card' }) {
  const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const [propertyId, setPropertyId] = useState(defaultPropertyId || '');
  const [date, setDate] = useState(yesterday());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    if (!propertyId && propertyOptions?.length) setPropertyId(propertyOptions[0].property_id);
  }, [propertyOptions, propertyId]);

  const fetchSummary = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-pickup-report/${propertyId}?date=${date}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to load report');
        setSummary(null);
      } else {
        setSummary(data);
      }
    } catch (_e) {
      toast.error('Failed to load report');
    }
    setLoading(false);
  };

  useEffect(() => { fetchSummary();   }, [propertyId, date]);

  const downloadPDF = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-pickup-report/${propertyId}/pdf?date=${date}`, {
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to download PDF');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NECS_Daily_Report_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (_e) {
      toast.error('Download failed');
    }
  };

  const emailPDF = async () => {
    if (!propertyId) return;
    setEmailing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-pickup-report/${propertyId}/email?date=${date}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 503) {
          toast.error('Email service not configured — download the PDF instead.');
        } else {
          toast.error(data.detail || 'Failed to send email');
        }
      } else {
        toast.success(`Emailed to ${data.recipient}`);
      }
    } catch (_e) {
      toast.error('Email failed');
    }
    setEmailing(false);
  };

  return (
    <Card className="p-6 border-l-4 border-l-[#C9A962]" data-testid={dataTestId}>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <FileText className="h-5 w-5 text-[#C9A962]" /> DAILY QR PICKUP REPORT
          </h3>
          <p className="text-sm text-zinc-500">
            Per-unit pickup scans, agent performance, and embedded violation photos — branded PDF.
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">
            Auto-emailed daily at 07:00 UTC. Manual download / resend below.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          {propertyOptions?.length > 1 && (
            <div>
              <Label className="text-xs text-zinc-500">Property</Label>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger className="w-[180px]" data-testid={`${dataTestId}-property-select`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {propertyOptions.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-zinc-500">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-[160px]"
              data-testid={`${dataTestId}-date-input`}
            />
          </div>
          <Button
            variant="outline"
            onClick={fetchSummary}
            disabled={loading}
            data-testid={`${dataTestId}-refresh`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {summary ? (
        <>
          {summary.summary.total_scans === 0 && (
            <div className="mb-3 p-3 rounded-md border border-dashed border-zinc-300 bg-zinc-50 text-sm text-zinc-600" data-testid={`${dataTestId}-empty-state`}>
              No QR pickup scans recorded for this date yet. You can still download an empty branded report for your records.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid={`${dataTestId}-summary`}>
            {[
              ['Total scans', summary.summary.total_scans, 'text-white bg-[#2D2D2D]'],
              ['Completed', summary.summary.completed, 'text-white bg-green-600'],
              ['Missed', summary.summary.missed, 'text-white bg-amber-600'],
              ['Violations', summary.summary.violations, 'text-white bg-red-600'],
              ['Units serviced', summary.summary.units_serviced, 'text-[#2D2D2D] bg-[#C9A962]'],
            ].map(([label, value, cls]) => (
              <div key={label} className={`rounded-lg p-3 ${cls}`}>
                <p className="text-2xl font-black">{value}</p>
                <p className="text-xs opacity-80">{label}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={downloadPDF}
              className="font-bold"
              style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
              data-testid={`${dataTestId}-download`}
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Button
              variant="outline"
              onClick={emailPDF}
              disabled={emailing}
              className="border-[#C9A962] text-[#C9A962]"
              data-testid={`${dataTestId}-email`}
            >
              <Mail className="h-4 w-4 mr-2" /> {emailing ? 'Sending…' : 'Email PDF'}
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-zinc-500">{loading ? 'Loading…' : 'Select a date and property.'}</p>
      )}
    </Card>
  );
}
